import {
  DOMSerializer,
  type Fragment,
  type Node as ProseMirrorNode,
  type Slice,
} from '@tiptap/pm/model';
import { Plugin, PluginKey, type EditorState } from '@tiptap/pm/state';
import { CellSelection, TableMap } from 'prosemirror-tables';

import type { Editor } from '@tiptap/core';

const PIPE_RE = /\|/g;
const NEWLINE_RE = /[\r\n]+/g;

/**
 * Escapes pipes and collapses newlines so a string fits on a single
 * markdown-table row.
 */
export function escapeMarkdownCell(text: string): string {
  return text.replace(PIPE_RE, '\\|').replace(NEWLINE_RE, ' ');
}

export interface ExtractedTable {
  rows: string[][];
  /**
   * True when row 0 is the real header (either selected, or prepended from
   * the source table).
   */
  headerInFirstRow: boolean;
}

export interface ExtractTableOptions {
  /**
   * Header cells from the source table for the selected column range. When
   * the selection doesn't already include the header, this gets prepended.
   */
  sourceHeader?: string[];
  /**
   * How to render a cell. Default uses textContent and drops marks.
   */
  serializeCell?: (cell: ProseMirrorNode) => string;
}

const defaultSerializeCell = (cell: ProseMirrorNode): string =>
  escapeMarkdownCell(cell.textContent);

/**
 * Flattens rows into a rectangular grid. colspan -> content in the first
 * column, rest empty. rowspan -> column reserved in following rows. Without
 * this, a row with a merged cell sizes its separator differently from its
 * siblings and the markdown table breaks.
 */
function rectangularizeRows(
  rowNodes: ProseMirrorNode[],
  serializeCell: (cell: ProseMirrorNode) => string
): string[][] {
  if (!rowNodes.length) return [];

  let totalCols = 0;
  for (const rowNode of rowNodes) {
    let cols = 0;
    rowNode.forEach((cell) => {
      cols += (cell.attrs.colspan as number | undefined) ?? 1;
    });
    if (cols > totalCols) totalCols = cols;
  }
  if (totalCols === 0) return [];

  // Per-column rows-remaining counter for cells with rowspan > 1.
  const occupied = new Array<number>(totalCols).fill(0);

  return rowNodes.map((rowNode, rowIdx) => {
    if (rowIdx > 0) {
      for (let c = 0; c < totalCols; c++) {
        if (occupied[c] > 0) occupied[c]--;
      }
    }

    const row: string[] = new Array<string>(totalCols).fill('');
    let cellIdx = 0;
    let col = 0;
    while (col < totalCols) {
      if (occupied[col] > 0) {
        col++;
        continue;
      }
      if (cellIdx >= rowNode.childCount) break;
      const cell = rowNode.child(cellIdx);
      const colspan = (cell.attrs.colspan as number | undefined) ?? 1;
      const rowspan = (cell.attrs.rowspan as number | undefined) ?? 1;
      row[col] = serializeCell(cell);
      if (rowspan > 1) {
        for (let c = col; c < col + colspan && c < totalCols; c++) {
          occupied[c] = rowspan;
        }
      }
      col += colspan;
      cellIdx++;
    }
    return row;
  });
}

/**
 * Walks a clipboard slice into a normalized table representation. Accepts
 * both full-table slices and Fragments of bare `tableRow` nodes (which is
 * what `CellSelection.content()` returns for partial selections). Returns
 * null when the slice doesn't look like table content.
 */
export function extractTableFromSlice(
  slice: Slice,
  options?: ExtractTableOptions
): ExtractedTable | null {
  const serializeCell = options?.serializeCell ?? defaultSerializeCell;
  const rowNodes: ProseMirrorNode[] = [];
  const first = slice.content.firstChild;
  if (first?.type.name === 'table') {
    for (let i = 0; i < first.childCount; i++) {
      rowNodes.push(first.child(i));
    }
  } else {
    let onlyTableRows = true;
    slice.content.forEach((node) => {
      if (node.type.name === 'tableRow') rowNodes.push(node);
      else onlyTableRows = false;
    });
    if (!onlyTableRows) return null;
  }
  if (!rowNodes.length) return null;

  const rows = rectangularizeRows(rowNodes, serializeCell);
  if (!rows.length) return null;

  let headerInFirstRow = false;
  rowNodes[0].forEach((cell) => {
    if (cell.type.name === 'tableHeader') headerInFirstRow = true;
  });

  const sourceHeader = options?.sourceHeader;
  const isSingleCell = rows.length === 1 && rows[0].length === 1;
  if (
    !headerInFirstRow &&
    !isSingleCell &&
    sourceHeader &&
    sourceHeader.length === rows[0].length
  ) {
    return {
      rows: [sourceHeader, ...rows],
      headerInFirstRow: true,
    };
  }

  return { rows, headerInFirstRow };
}

/**
 * Renders an extracted table as the `text/plain` clipboard payload.
 * Single cell becomes a plain value. Body-only single column becomes
 * values per line. Everything else becomes a markdown table with row 0
 * as the header.
 */
export function formatTableAsText({
  rows,
  headerInFirstRow,
}: ExtractedTable): string {
  if (!rows.length) return '';
  if (rows.length === 1 && rows[0].length === 1) return rows[0][0];
  if (rows.every((r) => r.length === 1) && !headerInFirstRow) {
    return rows.map((r) => r[0]).join('\n');
  }
  const colCount = rows[0].length;
  const separator =
    '| ' + Array.from({ length: colCount }, () => '---').join(' | ') + ' |';
  const lines = ['| ' + rows[0].join(' | ') + ' |', separator];
  for (let i = 1; i < rows.length; i++) {
    lines.push('| ' + rows[i].join(' | ') + ' |');
  }
  return lines.join('\n');
}

/**
 * HTML mirror of `formatTableAsText`. Rows hold cell HTML from
 * `makeHTMLCellSerializer`. Only emits `<thead>`/`<th>` when the selection
 * actually contains the real header — otherwise everything lands in
 * `<tbody>` with `<td>` so rich destinations (Docs, Notion, email) don't
 * promote the first data row to a header.
 */
export function formatTableAsHTML({
  rows,
  headerInFirstRow,
}: ExtractedTable): string {
  if (!rows.length) return '';
  if (rows.length === 1 && rows[0].length === 1) return rows[0][0];
  if (rows.every((r) => r.length === 1) && !headerInFirstRow) {
    const body = rows.map((r) => `<tr><td>${r[0]}</td></tr>`).join('');
    return `<table><tbody>${body}</tbody></table>`;
  }
  if (!headerInFirstRow) {
    const body = rows
      .map((r) => `<tr>${r.map((c) => `<td>${c}</td>`).join('')}</tr>`)
      .join('');
    return `<table><tbody>${body}</tbody></table>`;
  }
  const head = `<thead><tr>${rows[0]
    .map((c) => `<th>${c}</th>`)
    .join('')}</tr></thead>`;
  const bodyRows = rows
    .slice(1)
    .map((r) => `<tr>${r.map((c) => `<td>${c}</td>`).join('')}</tr>`)
    .join('');
  const body = bodyRows ? `<tbody>${bodyRows}</tbody>` : '';
  return `<table>${head}${body}</table>`;
}

/**
 * Reads the current CellSelection straight from the source table via
 * TableMap so every cell lands at its real column, not at its slice index.
 * Returns null when any cell spans across a rect edge — those layouts
 * can't be flattened to markdown without shifting or dropping data, so
 * callers should fall back to default copy.
 */
export function extractCellSelectionTable(
  state: EditorState,
  options?: ExtractTableOptions
): ExtractedTable | null {
  const sel = state.selection;
  if (!(sel instanceof CellSelection)) return null;
  const table = sel.$anchorCell.node(-1);
  if (!table || table.type.name !== 'table') return null;

  const map = TableMap.get(table);
  const tableStart = sel.$anchorCell.start(-1);
  const rect = map.rectBetween(
    sel.$anchorCell.pos - tableStart,
    sel.$headCell.pos - tableStart
  );
  const width = rect.right - rect.left;
  const height = rect.bottom - rect.top;
  if (width <= 0 || height <= 0) return null;

  const serializeCell = options?.serializeCell ?? defaultSerializeCell;
  const grid: string[][] = Array.from({ length: height }, () =>
    new Array<string>(width).fill('')
  );

  const seen = new Set<number>();
  let headerInFirstRow = false;

  for (let row = rect.top; row < rect.bottom; row++) {
    for (let col = rect.left; col < rect.right; col++) {
      const pos = map.map[row * map.width + col];
      if (seen.has(pos)) continue;
      seen.add(pos);
      const cellRect = map.findCell(pos);
      // Fail closed on any span crossing the rect edge.
      if (
        cellRect.top < rect.top ||
        cellRect.bottom > rect.bottom ||
        cellRect.left < rect.left ||
        cellRect.right > rect.right
      ) {
        return null;
      }
      const cell = table.nodeAt(pos);
      if (!cell) return null;
      if (row === rect.top && cell.type.name === 'tableHeader') {
        headerInFirstRow = true;
      }
      grid[row - rect.top][col - rect.left] = serializeCell(cell);
    }
  }

  const sourceHeader = options?.sourceHeader;
  const isSingleCell = grid.length === 1 && grid[0].length === 1;
  if (
    !headerInFirstRow &&
    !isSingleCell &&
    sourceHeader &&
    sourceHeader.length === grid[0].length
  ) {
    return {
      rows: [sourceHeader, ...grid],
      headerInFirstRow: true,
    };
  }

  return { rows: grid, headerInFirstRow };
}

/**
 * Pulls the source table's header cells for the columns covered by the
 * current CellSelection, looking them up by logical column via TableMap.
 * Returns null when a selected column's row-0 cell isn't a `tableHeader`
 * (so mixed first rows don't synthesize a header from non-header data),
 * or when a merged header straddles the selection edge.
 */
export function getSourceHeaderForSelection(
  state: EditorState,
  options?: { serializeCell?: (cell: ProseMirrorNode) => string }
): string[] | null {
  const serializeCell = options?.serializeCell ?? defaultSerializeCell;
  const sel = state.selection;
  if (!(sel instanceof CellSelection)) return null;

  const sourceTable = sel.$anchorCell.node(-1);
  if (!sourceTable || sourceTable.type.name !== 'table') return null;
  if (sourceTable.childCount === 0) return null;

  const map = TableMap.get(sourceTable);
  const tableStart = sel.$anchorCell.start(-1);
  const rect = map.rectBetween(
    sel.$anchorCell.pos - tableStart,
    sel.$headCell.pos - tableStart
  );

  const headerCells: string[] = [];
  const seen = new Set<number>();
  for (let col = rect.left; col < rect.right; col++) {
    const pos = map.map[col]; // row 0 column entries
    const cellRect = map.findCell(pos);
    // A merged header that spills past the column range can't be safely
    // flattened.
    if (cellRect.left < rect.left || cellRect.right > rect.right) {
      return null;
    }
    if (seen.has(pos)) {
      headerCells.push(''); // colspan continuation slot
      continue;
    }
    seen.add(pos);
    const cell = sourceTable.nodeAt(pos);
    // Per-column gate: every selected column's row-0 cell must actually
    // be a tableHeader. Otherwise a mixed first row would synthesize a
    // markdown/HTML header from regular tableCell data.
    if (!cell || cell.type.name !== 'tableHeader') return null;
    headerCells.push(serializeCell(cell));
  }
  return headerCells.length ? headerCells : null;
}

type MarkdownSerializer = { serialize: (content: Fragment) => string };

function readMarkdownSerializer(editor: Editor): MarkdownSerializer | null {
  const storage = editor.storage as {
    markdown?: { serializer?: MarkdownSerializer };
  };
  return storage.markdown?.serializer ?? null;
}

/**
 * Renders a cell as inline markdown (preserves bold/italic/code/link).
 * Falls back to textContent when tiptap-markdown isn't loaded.
 */
export function makeMarkdownCellSerializer(
  editor: Editor
): (cell: ProseMirrorNode) => string {
  const serializer = readMarkdownSerializer(editor);
  if (!serializer) {
    return (cell) => escapeMarkdownCell(cell.textContent);
  }
  return (cell) => escapeMarkdownCell(serializer.serialize(cell.content));
}

/**
 * Renders a cell as HTML for the `text/html` clipboard payload.
 */
export function makeHTMLCellSerializer(
  editor: Editor
): (cell: ProseMirrorNode) => string {
  const serializer = DOMSerializer.fromSchema(editor.schema);
  return (cell) => {
    const container = document.createElement('div');
    container.appendChild(serializer.serializeFragment(cell.content));
    return container.innerHTML;
  };
}

export interface ClipboardPayload {
  text: string;
  html: string;
}

/**
 * Builds the `{ text, html }` pair that both Cmd+C and the dropdown Copy
 * action write. Returns null for non-CellSelection or non-table slices so
 * callers can defer to default behavior.
 */
export function buildClipboardPayload(editor: Editor): ClipboardPayload | null {
  const selection = editor.state.selection;
  if (!(selection instanceof CellSelection)) return null;

  const textSerialize = makeMarkdownCellSerializer(editor);
  const htmlSerialize = makeHTMLCellSerializer(editor);

  const textHeader =
    getSourceHeaderForSelection(editor.state, {
      serializeCell: textSerialize,
    }) ?? undefined;
  const htmlHeader =
    getSourceHeaderForSelection(editor.state, {
      serializeCell: htmlSerialize,
    }) ?? undefined;

  // Returns null for ambiguous layouts; let the default copy run then.
  const textExtracted = extractCellSelectionTable(editor.state, {
    sourceHeader: textHeader,
    serializeCell: textSerialize,
  });
  const htmlExtracted = extractCellSelectionTable(editor.state, {
    sourceHeader: htmlHeader,
    serializeCell: htmlSerialize,
  });
  if (!textExtracted || !htmlExtracted) return null;

  return {
    text: formatTableAsText(textExtracted),
    html: formatTableAsHTML(htmlExtracted),
  };
}

export const tableClipboardTextPluginKey = new PluginKey('tableClipboardText');

/**
 * Intercepts the native copy event for CellSelections to write both
 * `text/plain` (markdown) and `text/html` (real `<table>`). The
 * `clipboardTextSerializer` is kept as a fallback for non-CellSelection
 * paths (e.g. NodeSelection on a whole table).
 */
export const tableClipboardTextPlugin = (editor: Editor) =>
  new Plugin({
    key: tableClipboardTextPluginKey,
    props: {
      handleDOMEvents: {
        copy: (_view, event) => {
          const payload = buildClipboardPayload(editor);
          if (!payload || !event.clipboardData) return false;
          event.preventDefault();
          event.clipboardData.setData('text/plain', payload.text);
          event.clipboardData.setData('text/html', payload.html);
          return true;
        },
      },
      clipboardTextSerializer: (slice) => {
        const sel = editor.state.selection;
        const serializeCell = makeMarkdownCellSerializer(editor);
        // Same fail-closed path as handleDOMEvents.copy.
        if (sel instanceof CellSelection) {
          const sourceHeader =
            getSourceHeaderForSelection(editor.state, { serializeCell }) ??
            undefined;
          const extracted = extractCellSelectionTable(editor.state, {
            sourceHeader,
            serializeCell,
          });
          if (extracted) return formatTableAsText(extracted);
          // Unsupported layout: defer to the default plain-text serialization.
          return slice.content.textBetween(0, slice.content.size, '\n\n');
        }
        // Non-CellSelection: best-effort slice extraction.
        const extracted = extractTableFromSlice(slice, { serializeCell });
        if (extracted) return formatTableAsText(extracted);
        const serializer = readMarkdownSerializer(editor);
        if (serializer) return serializer.serialize(slice.content);
        return slice.content.textBetween(0, slice.content.size, '\n\n');
      },
    },
  });
